import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Box,
} from '@mui/material';
import { Printer as FaxIcon } from 'lucide-react';
import { sendFax } from '../../utils/faxApi';

const E164_HELP = 'Use E.164 format, e.g. +15551234567';

/**
 * Modal to send an appeal letter PDF via fax.
 * @param {boolean} open - Whether the modal is open
 * @param {function} onClose - Called when modal closes (optionally with created fax transmission)
 * @param {Object} appeal - { id: string, appeal_letter?: string, claim?: { claim_number } }
 * @param {string} defaultFromNumber - Optional default sender number from config
 */
export default function SendFaxModal({ open, onClose, appeal, defaultFromNumber = '' }) {
  const [toNumber, setToNumber] = useState('');
  const [fromNumber, setFromNumber] = useState(defaultFromNumber);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = useCallback(() => {
    setError(null);
    setToNumber('');
    setFromNumber(defaultFromNumber);
    onClose?.();
  }, [onClose, defaultFromNumber]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!appeal?.id) return;
    setError(null);
    setLoading(true);
    try {
      const payload = {
        document_id: appeal.id,
        to_number: toNumber.trim(),
      };
      if (fromNumber.trim()) payload.from_number = fromNumber.trim();
      const transmission = await sendFax(payload);
      handleClose();
      onClose?.(transmission);
    } catch (err) {
      const msg = err.response?.data?.to_number?.[0]
        || err.response?.data?.from_number?.[0]
        || err.response?.data?.document_id?.[0]
        || err.response?.data?.error
        || err.message
        || 'Failed to send fax';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const hasPdf = appeal?.appeal_letter;
  const claimNumber = appeal?.claim?.claim_number || 'N/A';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <FaxIcon size={16} /> Send appeal letter by fax
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {!hasPdf && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This appeal does not have a PDF yet. Generate the appeal letter first.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Appeal for claim: <strong>{claimNumber}</strong>
          </Typography>
          <TextField
            fullWidth
            label="To (fax number)"
            value={toNumber}
            onChange={(e) => setToNumber(e.target.value)}
            placeholder="+15551234567"
            helperText={E164_HELP}
            required
            disabled={!hasPdf}
            sx={{ mb: 2 }}
            inputProps={{ 'aria-label': 'Recipient fax number' }}
          />
          <TextField
            fullWidth
            label="From (sender number)"
            value={fromNumber}
            onChange={(e) => setFromNumber(e.target.value)}
            placeholder="+15559876543"
            helperText={defaultFromNumber ? 'Override default sender' : 'Required if no default is set'}
            disabled={!hasPdf}
            sx={{ mb: 2 }}
            inputProps={{ 'aria-label': 'Sender fax number' }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !hasPdf || !toNumber.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : <FaxIcon size={16} />}
          >
            {loading ? 'Sending…' : 'Send fax'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
